import { useParams, Link } from 'react-router-dom';
import { getComicById } from '@/mocks/comics';
import { reviews } from '@/mocks/reviews';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/base/ScrollToTop';
import Breadcrumb from '@/components/base/Breadcrumb';

const COMIC_PAGE_IMAGES = [
  'https://readdy.ai/api/search-image?query=Manga%20comic%20page%20with%20dark%20city%20skyline%20at%20night%2C%20mysterious%20fog%20rolling%20in%2C%20main%20character%20silhouette%20on%20rooftop%2C%20dramatic%20panel%20layout%2C%20speech%20bubbles%2C%20professional%20manga%20art%20style%2C%20vertical%20format%2C%20dark%20atmospheric%20colors&width=900&height=1300&seq=comic-page-01&orientation=portrait',
  'https://readdy.ai/api/search-image?query=Manga%20comic%20page%2C%20character%20awakening%20power%20scene%2C%20glowing%20eyes%20and%20energy%20aura%2C%20dynamic%20action%20panels%2C%20urban%20alleyway%20setting%2C%20dramatic%20speed%20lines%2C%20professional%20manga%20art%2C%20vertical%20format&width=900&height=1300&seq=comic-page-02&orientation=portrait',
  'https://readdy.ai/api/search-image?query=Manga%20comic%20page%2C%20intense%20battle%20scene%20between%20two%20characters%2C%20energy%20clash%20effects%2C%20debris%20flying%2C%20multiple%20action%20panels%2C%20professional%20manga%20illustration%2C%20vertical%20format%2C%20dramatic%20composition&width=900&height=1300&seq=comic-page-03&orientation=portrait',
  'https://readdy.ai/api/search-image?query=Manga%20comic%20page%2C%20mysterious%20stranger%20appearing%20from%20shadows%2C%20dialogue%20heavy%20scene%2C%20nighttime%20street%20setting%2C%20lamppost%20lighting%2C%20speech%20bubbles%2C%20professional%20manga%20art%2C%20vertical%20format&width=900&height=1300&seq=comic-page-04&orientation=portrait',
  'https://readdy.ai/api/search-image?query=Manga%20comic%20page%2C%20characters%20forming%20team%2C%20group%20shot%20of%20four%20characters%2C%20determined%20expressions%2C%20urban%20rooftop%20at%20sunset%2C%20dramatic%20composition%2C%20professional%20manga%20style%2C%20vertical%20format&width=900&height=1300&seq=comic-page-05&orientation=portrait',
];

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const comic = getComicById(Number(id));

  if (!comic) {
    return (
      <>
        <ScrollToTop />
        <div className="min-h-screen bg-background-50">
          <Navbar />
          <main className="pt-20">
            <div className="flex flex-col items-center justify-center py-32 px-4">
              <div className="w-20 h-20 rounded-full bg-background-100 flex items-center justify-center mb-6">
                <i className="ri-emotion-sad-line text-foreground-500 text-3xl"></i>
              </div>
              <h1 className="text-xl font-heading font-bold text-foreground-100 mb-2">漫画未找到</h1>
              <p className="text-foreground-500 text-sm mb-6">该漫画不存在或已被移除</p>
              <Link to="/" className="px-5 py-2.5 bg-primary-500 hover:bg-primary-600 text-background-50 dark:text-foreground-950 rounded-full text-sm font-medium transition-colors whitespace-nowrap">
                返回首页
              </Link>
            </div>
          </main>
          <Footer />
        </div>
      </>
    );
  }

  const comicReviews = reviews.filter((r) => r.comicTitle === comic.title).slice(0, 4);

  const breadcrumbItems = [
    { label: '首页', href: '/' },
    { label: '漫画分类', href: '/category' },
    { label: comic.category, href: `/category?cat=${comic.category}` },
    { label: comic.title },
  ];

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: `${comic.title} - 叮当漫画网`,
    url: `http://kjbooks.cn/article/${comic.id}`,
    author: {
      '@type': 'Person',
      name: comic.author,
    },
    publisher: {
      '@type': 'Organization',
      name: '叮当漫画网',
    },
    datePublished: '2026-07-01',
    dateModified: comic.chapters[0]?.updatedAt || '2026-07-08',
    description: comic.description,
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `http://kjbooks.cn/article/${comic.id}`,
    },
  };

  return (
    <>
      <ScrollToTop />
      <script type="application/ld+json">{JSON.stringify(articleSchema)}</script>

      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-20">
          <div className="w-full px-4 md:px-6 py-6">
            <div className="max-w-7xl mx-auto">
              <Breadcrumb items={breadcrumbItems} />
            </div>
          </div>

          <article className="w-full max-w-2xl mx-auto px-4">
            <div className="mb-2 md:mb-3 rounded-xl overflow-hidden bg-background-100">
              <img
                src={COMIC_PAGE_IMAGES[0]}
                alt={`${comic.title} - 第1页`}
                title={`${comic.title} - 下拉式免费阅读 - 叮当漫画网`}
                className="w-full h-auto"
                loading="eager"
              />
            </div>
            <div className="mb-2 md:mb-3 rounded-xl overflow-hidden bg-background-100">
              <img
                src={COMIC_PAGE_IMAGES[1]}
                alt={`${comic.title} - 第2页`}
                title={`${comic.title} - 下拉式免费阅读 - 叮当漫画网`}
                className="w-full h-auto"
                loading="eager"
              />
            </div>
            <div className="mb-2 md:mb-3 rounded-xl overflow-hidden bg-background-100">
              <img
                src={COMIC_PAGE_IMAGES[2]}
                alt={`${comic.title} - 第3页`}
                title={`${comic.title} - 下拉式免费阅读 - 叮当漫画网`}
                className="w-full h-auto"
                loading="lazy"
              />
            </div>
            <div className="mb-2 md:mb-3 rounded-xl overflow-hidden bg-background-100">
              <img
                src={COMIC_PAGE_IMAGES[3]}
                alt={`${comic.title} - 第4页`}
                title={`${comic.title} - 下拉式免费阅读 - 叮当漫画网`}
                className="w-full h-auto"
                loading="lazy"
              />
            </div>
            <div className="mb-2 md:mb-3 rounded-xl overflow-hidden bg-background-100">
              <img
                src={COMIC_PAGE_IMAGES[4]}
                alt={`${comic.title} - 第5页`}
                title={`${comic.title} - 下拉式免费阅读 - 叮当漫画网`}
                className="w-full h-auto"
                loading="lazy"
              />
            </div>

            <div className="text-center py-8">
              <p className="text-foreground-500 text-sm flex items-center justify-center gap-2">
                <i className="ri-arrow-down-line"></i>
                继续向下阅读更多精彩内容
                <i className="ri-arrow-down-line"></i>
              </p>
            </div>
          </article>

          <section className="w-full px-4 md:px-6 py-10 bg-background-100">
            <div className="max-w-4xl mx-auto">
              <span className="inline-block px-3 py-1 border border-background-300/50 text-foreground-400 text-xs rounded-full mb-4 whitespace-nowrap">
                章节信息
              </span>
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-100">
                {comic.title} · 最新章节
              </h2>
              <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="bg-background-50 rounded-xl p-5">
                  <h3 className="text-foreground-200 font-heading text-lg font-bold mb-2">作品信息</h3>
                  <div className="space-y-2 text-sm">
                    <p className="flex justify-between">
                      <span className="text-foreground-500">作者</span>
                      <span className="text-foreground-200">{comic.author}</span>
                    </p>
                    <p className="flex justify-between">
                      <span className="text-foreground-500">状态</span>
                      <span className="text-primary-500">{comic.status}</span>
                    </p>
                    <p className="flex justify-between">
                      <span className="text-foreground-500">分类</span>
                      <span className="text-foreground-200">{comic.category}</span>
                    </p>
                    <p className="flex justify-between">
                      <span className="text-foreground-500">评分</span>
                      <span className="text-accent-500 flex items-center gap-1">
                        <i className="ri-star-fill text-xs"></i>
                        {comic.rating}
                      </span>
                    </p>
                  </div>
                </div>
                <div className="bg-background-50 rounded-xl p-5">
                  <h3 className="text-foreground-200 font-heading text-lg font-bold mb-2">章节列表</h3>
                  <div className="space-y-2 max-h-40 overflow-y-auto">
                    {comic.chapters.map((ch) => (
                      <p key={ch.id} className="flex justify-between text-sm">
                        <span className="text-foreground-300">{ch.title}</span>
                        <span className="text-foreground-600">{ch.updatedAt}</span>
                      </p>
                    ))}
                  </div>
                </div>
              </div>
              <p className="mt-6 text-foreground-500 text-sm leading-relaxed bg-background-50 rounded-xl p-5">
                {comic.description}
              </p>
              <div className="mt-5 flex flex-wrap gap-2">
                {comic.tags.map((tag) => (
                  <span key={tag} className="px-3 py-1 bg-background-200/50 text-foreground-400 text-xs rounded-full whitespace-nowrap">{tag}</span>
                ))}
              </div>
            </div>
          </section>

          {comicReviews.length > 0 && (
            <section className="w-full px-4 md:px-6 py-10 bg-background-50">
              <div className="max-w-4xl mx-auto">
                <h3 className="text-xl font-heading font-bold text-foreground-100 mb-6">读者评论</h3>
                <div className="columns-1 sm:columns-2 gap-4 md:gap-5">
                  {comicReviews.map((review) => (
                    <div key={review.id} className="break-inside-avoid mb-4 md:mb-5 bg-background-100 rounded-xl p-5">
                      <div className="flex items-center gap-3 mb-3">
                        <img src={review.avatar} alt={review.username} className="w-8 h-8 rounded-full object-cover flex-shrink-0" loading="lazy" />
                        <div>
                          <p className="text-foreground-200 text-sm font-bold">{review.username}</p>
                          <p className="text-foreground-600 text-[10px]">{review.handle}</p>
                        </div>
                        <div className="ml-auto flex items-center gap-0.5">
                          {Array.from({ length: review.rating }).map((_, i) => (
                            <i key={i} className="ri-star-fill text-accent-500 text-[10px]"></i>
                          ))}
                        </div>
                      </div>
                      <p className="text-foreground-400 text-sm leading-relaxed">{review.content}</p>
                    </div>
                  ))}
                </div>
              </div>
            </section>
          )}

          <section className="w-full px-4 md:px-6 py-10 md:py-14 bg-background-100">
            <div className="max-w-2xl mx-auto text-center">
              <h3 className="text-xl md:text-2xl font-heading font-bold text-foreground-100">
                还想看更多精彩内容？
              </h3>
              <p className="mt-3 text-foreground-500 text-sm">
                探索叮当漫画网数千部优质漫画，全部免费阅读
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center mt-8">
                <Link
                  to="/category"
                  className="inline-flex items-center justify-center gap-2 px-7 py-3 bg-foreground-100 hover:bg-foreground-200 text-background-50 font-medium rounded-full transition-colors whitespace-nowrap cursor-pointer"
                >
                  探索全部漫画
                  <i className="ri-arrow-right-line"></i>
                </Link>
                <Link
                  to="/article/1"
                  className="inline-flex items-center justify-center gap-2 px-7 py-3 bg-primary-500 hover:bg-primary-600 text-background-50 dark:text-foreground-950 font-medium rounded-full transition-colors whitespace-nowrap cursor-pointer"
                >
                  阅读全球诡异时代
                  <i className="ri-book-open-line"></i>
                </Link>
              </div>
            </div>
          </section>
        </main>
        <Footer />
      </div>
    </>
  );
}